import { useCallback, useEffect, useState, type FormEvent } from 'react';
import type { OrgMembersResponseDto, OrgSubscriptionsResponseDto } from '@shared/api-dto';
import { useSession } from '../auth/AuthProvider';
import { toOutcome } from '../api/errors';
import { roleLabel, statusLabel } from '../labels';
import { OrgBrandingSection } from './OrgBrandingSection';

export interface OrgAdminPageProps {
  /** Products whose module roles this org can assign, e.g. ['calculator']. */
  productIds: string[];
  /** productId → role ids offered in the UI (mirrors the module's roles.ts). */
  moduleRoles?: Record<string, string[]>;
}

/** Map a subscription status to a semantic badge variant (purely presentational). */
function statusBadgeClass(status: string): string {
  if (status === 'active') return 'badge badge-success';
  if (status === 'on_trial') return 'badge badge-warning';
  if (status === 'past_due' || status === 'unpaid' || status === 'cancelled') return 'badge badge-danger';
  return 'badge';
}

/**
 * Organization administration: members, invites, subscriptions, seats and roles.
 *
 * Everything renders from the SERVER's permissions (/auth/me), never from a guessed role check —
 * so this one component is correct for owner, admin and member alike.
 */
export function OrgAdminPage({ productIds, moduleRoles = {} }: OrgAdminPageProps) {
  const { client, can, me } = useSession();
  const [members, setMembers] = useState<OrgMembersResponseDto | null>(null);
  const [subs, setSubs] = useState<OrgSubscriptionsResponseDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'member'>('member');
  const [inviteSeat, setInviteSeat] = useState('');
  const [inviteModuleRoles, setInviteModuleRoles] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const mayRead = can('member:list:read');
  const mayInvite = can('member:invite:create');
  const maySeat = can('seat:assignment:create');
  const mayRole = can('member:role:assign');

  const load = useCallback(async () => {
    try {
      const [m, s] = await Promise.all([client.orgMembers(), client.orgSubscriptions()]);
      setMembers(m);
      setSubs(s);
    } catch (err) {
      const o = toOutcome(err);
      setError(o.kind === 'not-permitted' ? null : 'לא ניתן לטעון את הארגון.');
    }
  }, [client]);

  useEffect(() => {
    if (mayRead) void load();
  }, [mayRead, load]);

  if (!mayRead) {
    return (
      <div className="huaca-state" data-testid="org-admin-forbidden">
        <h1>{me?.org.name}</h1>
        <p>אין לכם הרשאה לנהל את הארגון הזה.</p>
      </div>
    );
  }

  async function invite(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      // Group the flat role list back under its product. Roles are namespaced `{productId}.{role}`,
      // so the product is derivable — but derive it from productIds, never by splitting the string.
      const grouped: Record<string, string[]> = {};
      for (const productId of productIds) {
        const picked = (moduleRoles[productId] ?? []).filter((r) => inviteModuleRoles.includes(r));
        if (picked.length) grouped[productId] = picked;
      }
      await client.inviteMember(inviteEmail, inviteRole, {
        ...(inviteSeat ? { seatSubscriptionId: inviteSeat } : {}),
        ...(Object.keys(grouped).length ? { moduleRoles: grouped } : {}),
      });
      setInviteEmail('');
      setInviteSeat('');
      setInviteModuleRoles([]);
      setInviteRole('member');
      await load();
    } catch (err) {
      const o = toOutcome(err);
      // A 409 here is the capacity guard ("no seats left") — the admin needs the real text, not
      // a generic failure, because the fix (free a seat / buy more) is theirs to make.
      setError(o.kind === 'conflict' ? o.message : 'לא ניתן לשלוח את ההזמנה.');
    } finally {
      setBusy(false);
    }
  }

  /** Seats an invite can promise: granting subs only, and only those with room left. */
  const seatable = (subs?.subscriptions ?? []).filter(
    (s) => (s.status === 'active' || s.status === 'on_trial') && s.seatsUsed < s.seatLimit,
  );

  async function act(fn: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
    } catch (err) {
      const o = toOutcome(err);
      // NO_SEATS_AVAILABLE is the one an admin will actually hit — say what to do about it.
      setError(o.kind === 'conflict' ? o.message : 'הפעולה נכשלה.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="org-admin-page">
      <h1>{me?.org.name}</h1>

      <section>
        <h2>מנויים</h2>
        {subs?.subscriptions.length === 0 && <p data-testid="no-subs">אין עדיין מנויים.</p>}
        <ul className="huaca-subs">
          {subs?.subscriptions.map((s) => (
            <li key={s.subscriptionId} data-testid={`sub-${s.subscriptionId}`}>
              <span>
                {s.planId} — <span className={statusBadgeClass(s.status)} data-testid="sub-status">{statusLabel(s.status)}</span>{' '}
                — מושבים {s.seatsUsed}/{s.seatLimit}
                {s.provider === 'internal' && <span> (ניסיון חינם)</span>}
              </span>
              {s.status === 'on_trial' && s.trialEndsAt && (
                <span> — הניסיון מסתיים ב־{new Date(s.trialEndsAt).toLocaleDateString('he-IL')}</span>
              )}
            </li>
          ))}
        </ul>
      </section>

      <section>
        <h2>חברים</h2>
        <div className="huaca-table-wrap">
        <table>
          <thead>
            <tr>
              <th>אימייל</th>
              <th>תפקיד</th>
              <th>מושבים</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {members?.members.map((m) => (
              <tr key={m.userId} data-testid={`member-${m.userId}`}>
                <td>
                  <bdi>{m.email}</bdi>
                </td>
                <td>
                  <span className="badge">{roleLabel(m.rootRole)}</span>
                </td>
                <td>
                  {m.seats.length === 0 ? (
                    <span data-testid="member-no-seat">אין</span>
                  ) : (
                    m.seats.map((s) => s.productId).join(', ')
                  )}
                </td>
                <td>
                  {maySeat &&
                    subs?.subscriptions
                      .filter((s) => s.status === 'active' || s.status === 'on_trial')
                      .filter((s) => !m.seats.some((seat) => seat.subscriptionId === s.subscriptionId))
                      .map((s) => (
                        <button
                          key={s.subscriptionId}
                          type="button"
                          disabled={busy || s.seatsUsed >= s.seatLimit}
                          data-testid={`assign-${m.userId}-${s.subscriptionId}`}
                          onClick={() => void act(() => client.assignSeat(s.subscriptionId, m.userId))}
                        >
                          {s.seatsUsed >= s.seatLimit ? `${s.planId} (מלא)` : `הענקת מושב ${s.planId}`}
                        </button>
                      ))}
                  {maySeat &&
                    m.seats.map((seat) => (
                      <button
                        key={seat.subscriptionId}
                        type="button"
                        className="btn-danger"
                        disabled={busy}
                        data-testid={`release-${m.userId}-${seat.subscriptionId}`}
                        onClick={() => void act(() => client.releaseSeat(seat.subscriptionId, m.userId))}
                      >
                        הסרת מושב {seat.productId}
                      </button>
                    ))}
                  {mayRole &&
                    productIds.map((productId) =>
                      (moduleRoles[productId] ?? []).map((roleId) => {
                        const held = (m.moduleRoles[productId] ?? []).includes(roleId);
                        return (
                          <button
                            key={roleId}
                            type="button"
                            disabled={busy}
                            data-testid={`role-${m.userId}-${roleId}`}
                            onClick={() =>
                              void act(() =>
                                client.setModuleRoles(
                                  m.userId,
                                  productId,
                                  held
                                    ? (m.moduleRoles[productId] ?? []).filter((r) => r !== roleId)
                                    : [...(m.moduleRoles[productId] ?? []), roleId],
                                ),
                              )
                            }
                          >
                            {held ? `הסרת ${roleId}` : `הוספת ${roleId}`}
                          </button>
                        );
                      }),
                    )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        {/* The rule that trips everyone up: a role without a seat does nothing. Say it here. */}
        <p role="note">תפקידי המוצר של חבר נכנסים לתוקף רק כשיש לו מושב במוצר.</p>
      </section>

      {mayInvite && (
        <section>
          <h2>הזמנת אדם</h2>
          <form className="huaca-form" onSubmit={invite}>
            <label htmlFor="huaca-invite">אימייל</label>
            <input
              id="huaca-invite"
              type="email"
              required
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="colleague@company.com"
              dir="ltr"
            />

            <label htmlFor="huaca-invite-role">תפקיד בארגון</label>
            <select
              id="huaca-invite-role"
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as 'admin' | 'member')}
            >
              <option value="member">חבר</option>
              <option value="admin">מנהל</option>
            </select>

            {/* Promising the seat NOW is what makes their first login land in the product. Without
                it they join, hit NO_SEAT, and wait on a second admin action. */}
            {maySeat && (
              <>
                <label htmlFor="huaca-invite-seat">הענקת מושב</label>
                <select
                  id="huaca-invite-seat"
                  value={inviteSeat}
                  onChange={(e) => setInviteSeat(e.target.value)}
                  data-testid="invite-seat"
                >
                  <option value="">ללא מושב בינתיים</option>
                  {seatable.map((s) => (
                    <option key={s.subscriptionId} value={s.subscriptionId}>
                      {s.planId} ({s.seatLimit - s.seatsUsed} מתוך {s.seatLimit} פנויים)
                    </option>
                  ))}
                </select>
                {seatable.length === 0 && (
                  <p role="note" data-testid="no-seatable">
                    כל המושבים תפוסים, ולכן הם יצטרפו ללא מושב. פנו מושב או הוסיפו תוכנית קודם.
                  </p>
                )}
              </>
            )}

            {mayRole &&
              productIds.map((productId) =>
                (moduleRoles[productId] ?? []).map((roleId) => (
                  <label key={roleId} className="huaca-check" htmlFor={`huaca-invite-role-${roleId}`}>
                    <input
                      id={`huaca-invite-role-${roleId}`}
                      type="checkbox"
                      data-testid={`invite-role-${roleId}`}
                      checked={inviteModuleRoles.includes(roleId)}
                      onChange={(e) =>
                        setInviteModuleRoles((prev) =>
                          e.target.checked ? [...prev, roleId] : prev.filter((r) => r !== roleId),
                        )
                      }
                    />
                    {roleId}
                  </label>
                )),
              )}

            <button type="submit" className="btn-cta" disabled={busy} data-testid="invite-submit">
              שליחת הזמנה
            </button>
          </form>
          {members && members.pendingInvites.length > 0 && (
            <>
              <h3>הזמנות ממתינות</h3>
              <ul className="huaca-invites">
                {members.pendingInvites.map((i) => (
                  <li key={i.email} data-testid={`invite-${i.email}`}>
                    <bdi>{i.email}</bdi> — {roleLabel(i.rootRole)} — פג תוקף ב־
                    {new Date(i.expiresAt).toLocaleDateString('he-IL')}
                    {/* A promised seat counts against capacity before they ever log in, so an admin
                        wondering "why is my 5-seat plan full?" can see the answer here. */}
                    {i.seatSubscriptionId && (
                      <span data-testid={`invite-seat-${i.email}`}>
                        {' '}
                        — מחזיק במושב{' '}
                        {subs?.subscriptions.find((s) => s.subscriptionId === i.seatSubscriptionId)?.planId ??
                          'תוכנית'}
                      </span>
                    )}
                    <button
                      type="button"
                      className="btn-danger huaca-invite-revoke"
                      disabled={busy}
                      data-testid={`revoke-${i.email}`}
                      onClick={() => void act(() => client.revokeInvite(i.email))}
                    >
                      ביטול
                    </button>
                  </li>
                ))}
              </ul>
              {/* They join on ANY login, not only via the link — worth telling the admin. */}
              <p role="note">המוזמנים מצטרפים אוטומטית בפעם הבאה שיתחברו עם אותה כתובת.</p>
            </>
          )}
        </section>
      )}

      {error && (
        <p role="alert" data-testid="org-admin-error">
          {error}
        </p>
      )}

      <OrgBrandingSection />
    </div>
  );
}
